import { lazy, Suspense } from 'react'
import type { ComponentType } from 'react'

import { LoadingRegion } from 'src/components/ui/LoadingRegion'
import { wasJustReloadedForStaleChunk } from 'src/lib/reloadOnStaleChunk'

const STALE_CHUNK_RELOAD_DESCRIPTION =
  'The app has been updated. Loading the latest version…'

// The outermost suspended wrapper renders straight into the full-height app
// root, so the fallback fills the viewport; a blank fallback there reads as
// an outage on slow networks, most often right after a post-deploy reload.
const SetWrapFallback = () => (
  <LoadingRegion
    description={
      wasJustReloadedForStaleChunk(Date.now())
        ? STALE_CHUNK_RELOAD_DESCRIPTION
        : undefined
    }
  />
)

// Wraps an app-specific layout/provider/route-wrapper in React.lazy for use
// as a `<Set wrap={...}>` component in Routes.tsx. A top-level import there
// would pull the module and its transitive side effects (ag-grid
// registration, stylesheets, icon sets, …) into the entry bundle of all three
// apps — even though the per-app route trees are dead-code-eliminated —
// because the bundler must preserve imported modules' side effects.
//
// Each lazy component carries its own Suspense boundary since `<Set wrap>`
// components render outside the router's per-page Suspense. The boundary
// shows a loading state while the chunk downloads (see SetWrapFallback).
//
// This helper must live outside Routes.tsx: Cedar's routes auto-loader
// injects its own `import { lazy } from 'react'` plus generated page specs at
// the top of that file, and a user-land react import there collides with it
// under Vite's dev-server CJS interop ("Cannot access 'lazy' before
// initialization").
//
// `ComponentType<never>` accepts any component (props are contravariant); the
// wrapped components take heterogeneous props that `Set` passes through, so
// the prop type is deliberately erased rather than threaded generically.
// A generic `<P>(load: () => Promise<{ default: ComponentType<P> }>) =>
// ComponentType<P>` was tried and fails two ways: TS infers a
// `ComponentType<never> | ...` union for P at the `.then`-mapped callsites,
// and Cedar `<Set wrap>`'s WrapperType inference then rejects the precisely
// typed wrappers at every usage ("ComponentType<X> is not assignable to
// ComponentType<X> & WrapperType<...>").
export const lazySetWrap = (
  load: () => Promise<{ default: ComponentType<never> }>
) => {
  const Component = lazy(
    load as unknown as () => Promise<{
      default: ComponentType<Record<string, unknown>>
    }>
  )
  const LazySetWrap = (props: Record<string, unknown>) => (
    <Suspense fallback={<SetWrapFallback />}>
      <Component {...props} />
    </Suspense>
  )
  return LazySetWrap
}
